/**
 * 选区蒙版（M2-06 / DESIGN.md §5.1）。
 *
 * §5.1：选区 = **8bit 灰度蒙版（分块存储）**。0 完全未选、255 完全选中、
 * 中间值是羽化与抗锯齿的本体——路径表达不了这些。
 *
 * 稀疏性与 TileStore 同一套哲学，但存储独立：选区是单通道，塞进 RGBA 的
 * TileStore 要浪费四分之三的内存，且选区不参与合成器的像素管线。
 *
 * **base 值**是稀疏表示的关键：未分配块的值由 `base` 决定（0 或 255）。
 * 空选区 = base 0 + 零个块；全选 = base 255 + 零个块。反选只翻转 base 与
 * 已有块，成本与文档尺寸无关。
 *
 * 蒙版**不可变**：所有运算返回新蒙版。这让「取消后蒙版不变」（验收标准 4）
 * 成为结构性事实，也让选区历史可以只存引用。
 */
import { TILE } from '../tile/key';

/** 单块字节数（单通道）。 */
export const MASK_TILE_BYTES = TILE * TILE;

function tileId(tx: number, ty: number): string {
  return `${tx} ${ty}`;
}

export class SelectionMask {
  /** 文档空间宽高（像素）。 */
  readonly width: number;
  readonly height: number;
  /** 未分配块的值。0=未选，255=已选。 */
  readonly base: 0 | 255;
  readonly #tiles: ReadonlyMap<string, Uint8Array>;

  private constructor(
    width: number,
    height: number,
    base: 0 | 255,
    tiles: ReadonlyMap<string, Uint8Array>,
  ) {
    this.width = width;
    this.height = height;
    this.base = base;
    this.#tiles = tiles;
  }

  /** 空选区。零分配。 */
  static empty(width: number, height: number): SelectionMask {
    return new SelectionMask(width, height, 0, new Map());
  }

  /** 全选。同样零分配——「全选」不该比「没选」贵。 */
  static all(width: number, height: number): SelectionMask {
    return new SelectionMask(width, height, 255, new Map());
  }

  /**
   * 由构建器产生蒙版。builder 往 tiles 里放的块会被**规范化**：
   * 与 base 全同的块直接丢弃，稀疏性不靠调用方自觉。
   */
  static build(
    width: number,
    height: number,
    base: 0 | 255,
    tiles: Map<string, Uint8Array>,
  ): SelectionMask {
    for (const [id, data] of [...tiles]) {
      let uniform = true;
      for (let i = 0; i < data.length; i++) {
        if (data[i] !== base) {
          uniform = false;
          break;
        }
      }
      if (uniform) tiles.delete(id);
    }
    return new SelectionMask(width, height, base, tiles);
  }

  /**
   * 整图灰度数组（长度 `width*height`，行主序）→ 稀疏蒙版。魔棒/选择主体
   * 这类"wasm 算出一整张灰度蒙版"的场景都要做同一件"整行 memcpy 切块"的
   * 事——抽出来是因为已经有两处调用（不是为了单一调用方预先抽象）。
   */
  static fromDenseGrayscale(width: number, height: number, dense: Uint8Array): SelectionMask {
    if (dense.length !== width * height) {
      throw new RangeError(`灰度数组长度 ${dense.length} 与 ${width}×${height} 不符。`);
    }
    const tiles = new Map<string, Uint8Array>();
    const tilesX = Math.ceil(width / TILE);
    const tilesY = Math.ceil(height / TILE);
    for (let ty = 0; ty < tilesY; ty++) {
      const maxY = Math.min(TILE, height - ty * TILE);
      for (let tx = 0; tx < tilesX; tx++) {
        const maxX = Math.min(TILE, width - tx * TILE);
        const data = new Uint8Array(TILE * TILE);
        for (let y = 0; y < maxY; y++) {
          const rowStart = (ty * TILE + y) * width + tx * TILE;
          data.set(dense.subarray(rowStart, rowStart + maxX), y * TILE);
        }
        tiles.set(`${tx} ${ty}`, data);
      }
    }
    return SelectionMask.build(width, height, 0, tiles);
  }

  /** 已分配块数。空选区与全选恒为 0（验收标准 1）。 */
  get allocatedTiles(): number {
    return this.#tiles.size;
  }

  /** 读一个像素（文档空间）。越界按 base 处理。 */
  valueAt(x: number, y: number): number {
    if (x < 0 || y < 0 || x >= this.width || y >= this.height) return this.base;
    const tile = this.#tiles.get(tileId(Math.floor(x / TILE), Math.floor(y / TILE)));
    if (tile === undefined) return this.base;
    return tile[(y % TILE) * TILE + (x % TILE)]!;
  }

  /** 读一块。不存在返回 null（值为 base）。 */
  tile(tx: number, ty: number): Uint8Array | null {
    return this.#tiles.get(tileId(tx, ty)) ?? null;
  }

  /** 有偏离 base 内容的块坐标列表。 */
  *tileCoords(): Generator<{ tx: number; ty: number }> {
    for (const id of this.#tiles.keys()) {
      const [tx, ty] = id.split(' ').map(Number) as [number, number];
      yield { tx, ty };
    }
  }

  /** 没有任何像素被选中。 */
  get isEmpty(): boolean {
    return this.base === 0 && this.#tiles.size === 0;
  }

  /** 全部像素被选中。 */
  get isAll(): boolean {
    return this.base === 255 && this.#tiles.size === 0;
  }

  /** 横向块数。 */
  get tilesX(): number {
    return Math.ceil(this.width / TILE);
  }

  get tilesY(): number {
    return Math.ceil(this.height / TILE);
  }

  /**
   * 把第 y 行的 [x0, x1) 读进 out 的前缀（长度 x1−x0）。
   * 越界部分按 base 填充。按块段 memcpy——流式卷积（feather/morph）
   * 每输出一行要读一行，逐像素 valueAt 会把行读取变成热点。
   */
  readRow(y: number, x0: number, x1: number, out: Uint8Array): void {
    const length = x1 - x0;
    out.fill(this.base, 0, length);
    if (y < 0 || y >= this.height) return;
    const ty = Math.floor(y / TILE);
    const rowOffset = (y % TILE) * TILE;
    let x = Math.max(0, x0);
    const end = Math.min(this.width, x1);
    while (x < end) {
      const tx = Math.floor(x / TILE);
      const segmentEnd = Math.min((tx + 1) * TILE, end);
      const tile = this.tile(tx, ty);
      if (tile !== null) {
        const local = x % TILE;
        out.set(tile.subarray(rowOffset + local, rowOffset + local + (segmentEnd - x)), x - x0);
      }
      x = segmentEnd;
    }
  }

  /**
   * 偏离 base 内容的外接矩形（文档空间），没有则为 null。
   * 羽化等运算用它把工作范围压到内容附近，而不是整个文档。
   */
  contentBounds(): { x: number; y: number; width: number; height: number } | null {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const { tx, ty } of this.tileCoords()) {
      minX = Math.min(minX, tx * TILE);
      minY = Math.min(minY, ty * TILE);
      maxX = Math.max(maxX, Math.min((tx + 1) * TILE, this.width));
      maxY = Math.max(maxY, Math.min((ty + 1) * TILE, this.height));
    }
    if (!Number.isFinite(minX)) return null;
    return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
  }
}
